import { FC, useMemo, useState } from "react";
import clsx from "clsx";

import { TrainingExercise, TrainingItem } from "../../model/types";

import styles from "./TrainingsList.module.scss";

type TrainingsListProps = {
  trainings: TrainingItem[];
  className?: string;
};

type TabKey = "all" | "upcoming" | "completed";

const formatDate = (value: string | null) => {
  if (!value) {
    return "Дата не указана";
  }

  try {
    const date = new Date(value);
    return new Intl.DateTimeFormat("ru-RU", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    }).format(date);
  } catch (error) {
    return value;
  }
};

const detectStatus = (schedule: string | null): TabKey => {
  if (!schedule) {
    return "all";
  }

  const date = new Date(schedule);
  const now = new Date();

  if (Number.isNaN(date.getTime())) {
    return "all";
  }

  return date >= now ? "upcoming" : "completed";
};

const renderExercise = (exercise: TrainingExercise) => {
  if (!exercise.results?.length) {
    return exercise.title;
  }

  const summary = exercise.results
    .map((result) => `${result.repetitions || "-"}×${result.weight || "-"}`)
    .join(", ");

  return `${exercise.title} — ${summary}`;
};

export const TrainingsList: FC<TrainingsListProps> = ({
  trainings,
  className,
}) => {
  const [activeTab, setActiveTab] = useState<TabKey>("all");

  const decorated = useMemo(
    () =>
      trainings.map((training) => ({
        ...training,
        status: detectStatus(training.schedule),
      })),
    [trainings],
  );

  const tabCounters = useMemo(() => {
    const counters: Record<TabKey, number> = {
      all: decorated.length,
      upcoming: decorated.filter((item) => item.status === "upcoming").length,
      completed: decorated.filter((item) => item.status === "completed").length,
    };

    return counters;
  }, [decorated]);

  const filtered = useMemo(() => {
    if (activeTab === "all") {
      return decorated;
    }

    return decorated.filter((item) => item.status === activeTab);
  }, [activeTab, decorated]);

  if (!trainings.length) {
    return (
      <div className={clsx(styles.wrapper, className)}>
        <div className={styles.empty}>Тренировок пока нет</div>
      </div>
    );
  }

  return (
    <div className={clsx(styles.wrapper, className)}>
      <div className={styles.header}>
        <h3 className={styles.title}>Программа тренировок</h3>

        <div className={styles.tabs}>
          <button
            type="button"
            className={clsx(
              styles.tab,
              activeTab === "all" && styles.tabActive,
            )}
            onClick={() => setActiveTab("all")}
          >
            Все ({tabCounters.all})
          </button>
          <button
            type="button"
            className={clsx(
              styles.tab,
              activeTab === "upcoming" && styles.tabActive,
            )}
            onClick={() => setActiveTab("upcoming")}
          >
            Предстоящие ({tabCounters.upcoming})
          </button>
          <button
            type="button"
            className={clsx(
              styles.tab,
              activeTab === "completed" && styles.tabActive,
            )}
            onClick={() => setActiveTab("completed")}
          >
            Завершённые ({tabCounters.completed})
          </button>
        </div>
      </div>

      <div className={styles.list}>
        {filtered.map((training) => (
          <div key={training.training_id} className={styles.item}>
            <div className={styles.itemHeader}>
              <div>
                <h4 className={styles.itemTitle}>{training.title}</h4>
                <span className={styles.itemDate}>
                  {formatDate(training.schedule)}
                </span>
              </div>
              <span
                className={clsx(
                  styles.badge,
                  training.status === "completed"
                    ? styles.badgeCompleted
                    : styles.badgeUpcoming,
                )}
              >
                {training.status === "completed" ? "Завершено" : "Предстоит"}
              </span>
            </div>

            {training.exercizes && (
              <div className={styles.exercises}>
                <span className={styles.exercisesTitle}>
                  Упражнения ({training.exercizes.length})
                </span>
                <ul>
                  {training.exercizes.map((exercise) => (
                    <li key={exercise.exercize_id}>{renderExercise(exercise)}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};


















